import { toScreen } from '@doltech/core/lib/responsive/responsive.util';
import { IconArrowsRefresh } from '@doltech/icons/IconArrowsRefresh';
import { IconSystemV2MediaRepeat } from '@doltech/icons/SystemV2/IconSystemV2MediaRepeat';
import { IconSystemV2MediaUnrepeat } from '@doltech/icons/SystemV2/IconSystemV2MediaUnrepeat';
import { ButtonIconPrimaryGeneral } from '@doltech/ui/lib/figma/Button/ButtonIconPrimaryGeneral/ButtonIconPrimaryGeneral';
import { ButtonVideoPlayerControlSpeedLight } from '@doltech/ui/lib/figma/Media/ButtonVideoPlayerControlSpeedLight/ButtonVideoPlayerControlSpeedLight';
import { MediaButton } from '@doltech/ui/lib/figma/Media/MediaButton/MediaButton';
import { AudioPlayerItemTimeline } from '@doltech/ui/lib/figma/ProgressBar/AudioPlayerItemTimeline/AudioPlayerItemTimeline';
import { ResponsiveTypography } from '@doltech/ui/lib/figma/Typography/v3/ResponsiveTypography';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { useControlledAudioPlayInBackgroundStore } from '@doltech/ui/lib/hooks/controlled-audio-play-in-background.store';
import { formatSecondToMMss } from '@doltech/utils/lib/date';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';
import { IconSystemV2MediaRepeat1Time } from '@doltech/icons/SystemV2/IconSystemV2MediaRepeat1Time';
import { IconSystemV2MediaRepeat2Times } from '@doltech/icons/SystemV2/IconSystemV2MediaRepeat2Times';

interface PlayerProps {
  url: string;
  className?: string;
  startTimeInSeconds: number;
  endTimeInSeconds: number;
  canSeek?: boolean;
  hasLoopTimes?: boolean;
}

const Container = styled.div`
  padding: 16px 20px;
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr) auto;
  align-items: center;
  gap: 20px;

  ${toScreen(760)} {
    gap: 12px;
    .volume,
    .replay {
      display: none;
    }
  }
`;
const ActionWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  gap: 12px;
`;
const Control = styled.div``;
const Progress = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  align-items: center;
  gap: 12px;
`;

const CurrentTime = styled.span`
  color: ${colorsV2.black80};
`;

export const Player = observer((props: PlayerProps) => {
  const {
    url,
    className,
    startTimeInSeconds,
    endTimeInSeconds,
    canSeek = false,
    hasLoopTimes = false,
  } = props;
  const audioStore = useControlledAudioPlayInBackgroundStore();

  React.useEffect(() => {
    audioStore.registerUrl(url);
    audioStore.setAudioTimeInSeconds(0);
  }, [audioStore, url]);

  const { currentTimeInSeconds, totalTimeInSeconds } = React.useMemo(() => {
    if (Number.isFinite(startTimeInSeconds) && Number.isFinite(endTimeInSeconds)) {
      return {
        currentTimeInSeconds: audioStore.audioTimeInSeconds
          ? audioStore.audioTimeInSeconds - startTimeInSeconds
          : audioStore.audioTimeInSeconds,
        totalTimeInSeconds: endTimeInSeconds - startTimeInSeconds,
      };
    }
    return {
      currentTimeInSeconds: audioStore.audioTimeInSeconds,
      totalTimeInSeconds: audioStore.durationInSeconds,
    };
  }, [
    endTimeInSeconds,
    startTimeInSeconds,
    audioStore.durationInSeconds,
    audioStore.audioTimeInSeconds,
  ]);

  React.useEffect(() => {
    if (hasLoopTimes) {
      if (
        (currentTimeInSeconds >= totalTimeInSeconds && audioStore.playing) ||
        audioStore.audioTimeInSeconds >= audioStore.durationInSeconds
      ) {
        switch (audioStore.loopTimes) {
          case 1:
          case 2:
          case 999999: {
            if (audioStore.playedTime === audioStore.loopTimes) {
              audioStore.seek(startTimeInSeconds);
              audioStore.pause();
              audioStore.setPlayedTime(0);
              return;
            }
            audioStore.seek(startTimeInSeconds);
            audioStore.setPlayedTime(audioStore.playedTime + 1);
            audioStore.play();
            break;
          }
          default:
            audioStore.seek(startTimeInSeconds);
            audioStore.pause();
            audioStore.setPlayedTime(0);
            break;
        }
        return;
      }
    } else {
      if (currentTimeInSeconds > totalTimeInSeconds) {
        audioStore.seek(startTimeInSeconds);
        if (!audioStore.loop) {
          audioStore.pause();
        }
      }
    }
  }, [audioStore, currentTimeInSeconds, totalTimeInSeconds, startTimeInSeconds]);

  const renderLoopIcon = React.useCallback(() => {
    switch (audioStore.loopTimes) {
      case 1:
        return <IconSystemV2MediaRepeat1Time size={20} />;
      case 2:
        return <IconSystemV2MediaRepeat2Times size={20} />;
      case 999999:
        return <IconSystemV2MediaRepeat size={20} />;
      default:
        return <IconSystemV2MediaUnrepeat size={20} />;
    }
  }, [audioStore.loopTimes]);

  const renderLoopIconOld = React.useCallback(() => {
    return audioStore.loop ? (
      <IconSystemV2MediaRepeat size={20} />
    ) : (
      <IconSystemV2MediaUnrepeat size={20} />
    );
  }, [audioStore.loop]);

  return (
    <Container className={cl('player', className)}>
      <ActionWrapper>
        <Control>
          {audioStore.playing ? (
            <MediaButton type="pause" onClick={() => audioStore.pause()} />
          ) : (
            <MediaButton
              type="play"
              onClick={() => {
                if (currentTimeInSeconds === 0) {
                  audioStore.seek(startTimeInSeconds);
                }
                audioStore.play();
              }}
            />
          )}
        </Control>
        <ButtonIconPrimaryGeneral
          className="replay"
          onClick={() => audioStore.seek(startTimeInSeconds)}
          type="gray"
          buttonStyle="outline"
          icon={<IconArrowsRefresh size={24} />}
          size="x-large"
        />
      </ActionWrapper>

      <Progress>
        <AudioPlayerItemTimeline
          currentTimeInSeconds={currentTimeInSeconds}
          durationInSeconds={totalTimeInSeconds}
          onSeek={audioStore.seek}
          canSeek={canSeek}
        />
        <ResponsiveTypography.Paragraph variant="medium/12-20" color="black50">
          <CurrentTime>
            {formatSecondToMMss(currentTimeInSeconds > 0 ? currentTimeInSeconds : 0)}
          </CurrentTime>
          <span>/</span>
          <span>{formatSecondToMMss(totalTimeInSeconds)}</span>
        </ResponsiveTypography.Paragraph>
      </Progress>
      <ActionWrapper>
        <ButtonVideoPlayerControlSpeedLight
          onSelect={audioStore.changePlaybackRate}
          options={[1, 0.75, 0.5]}
          defaultValue={audioStore.playbackRate}
        />
        <ButtonIconPrimaryGeneral
          className="loop"
          onClick={hasLoopTimes ? audioStore.setLoopTimes : audioStore.toggleLoop}
          type="gray"
          buttonStyle="outline"
          icon={hasLoopTimes ? renderLoopIcon() : renderLoopIconOld()}
          size="x-large"
        />
      </ActionWrapper>
    </Container>
  );
});
